import React from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { getIOSMode, needsIOSBrowserWorkaround } from "@/lib/iosDetection";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import { trpc } from "@/lib/trpc";
import { TrendingUp, BarChart3, Sparkles, Clock, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { Link } from "wouter";
import { VERSION, FIX_NUMBER } from "@shared/version";
import { soundManager } from "@/lib/soundManager";

export default function Home() {
  const { isAuthenticated, user } = useAuth();
  
  // Log iOS mode for debugging
  React.useEffect(() => {
    const mode = getIOSMode();
    if (mode !== 'not-ios') {
      console.log('[iOS Detection] Mode:', mode, 'Needs workaround:', needsIOSBrowserWorkaround());
    }
  }, []);
  
  // Add error handling to all queries
  const { data: recentDraws, isLoading, error: recentDrawsError } = trpc.lottery.getRecentDraws.useQuery(
    { limit: 5 },
    { retry: 1, staleTime: 30000 }
  );
  const { data: stats, error: statsError } = trpc.lottery.getStatistics.useQuery(
    undefined,
    { retry: 1, staleTime: 60000 }
  );
  const { data: lastUpdate, error: lastUpdateError } = trpc.lottery.getLastUpdate.useQuery(
    undefined,
    { retry: 1, staleTime: 30000 }
  );
  
  const [isUpdating, setIsUpdating] = useState(false);
  const utils = trpc.useUtils();

  const updateMutation = trpc.lottery.updateLatestDraws.useMutation({
    onSuccess: (data) => {
      if (data.newDraws > 0) {
        soundManager.playSuccess();
        toast.success(`Added ${data.newDraws} new draw(s)!`);
        // Refresh data
        utils.lottery.getRecentDraws.invalidate();
        utils.lottery.getStatistics.invalidate();
        utils.lottery.getLastUpdate.invalidate();
      } else {
        soundManager.playNotification();
        toast.info(data.message);
      }
      setIsUpdating(false);
    },
    onError: (error) => {
      soundManager.playError();
      toast.error(error.message || "Failed to check for updates");
      setIsUpdating(false);
    },
  });

  const handleCheckUpdates = () => {
    soundManager.playClick();
    setIsUpdating(true);
    updateMutation.mutate();
  };

  // Calculate time until next draw (9:00 PM)
  const getTimeUntilDraw = () => {
    const now = new Date();
    const nextDraw = new Date();
    nextDraw.setHours(21, 0, 0, 0);
    
    if (now.getHours() >= 21) {
      nextDraw.setDate(nextDraw.getDate() + 1);
    }
    
    const diff = nextDraw.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-background to-blue-950/20">
      <Navigation />
      
      <main className="flex-1 pb-20 md:pb-8">
        {/* Hero Section */}
        <div className="container mx-auto px-4 py-12">
          {/* User Welcome Badge */}
          {isAuthenticated && user && (
            <div className="flex justify-center mb-6">
              <Link href="/profile">
                <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-gradient-to-r from-primary/20 to-purple-500/20 border border-primary/40 hover:border-primary/60 transition-all cursor-pointer group">
                  {user.profilePicture ? (
                    <img
                      src={user.profilePicture}
                      alt={user.name || "Profile"}
                      className="w-8 h-8 rounded-full border-2 border-primary/40 object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center border-2 border-primary/40">
                      <span className="text-white text-xs font-bold">
                        {user.name?.charAt(0).toUpperCase() || "U"}
                      </span>
                    </div>
                  )}
                  <div className="text-left">
                    <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                      {user.name || "User"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {user.role === "admin" ? "Admin Account" : "Premium Account"}
                    </p>
                  </div>
                </div>
              </Link>
            </div>
          )}
          {!isAuthenticated && (
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/50 border border-border">
                <div className="w-2 h-2 rounded-full bg-muted-foreground animate-pulse"></div>
                <p className="text-sm text-muted-foreground">Free Account</p>
              </div>
            </div>
          )}

          <div className="text-center max-w-4xl mx-auto mb-12">
            <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-primary via-amber-300 to-primary bg-clip-text text-transparent animate-gradient">
              AI-Powered Lottery Predictions
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8">
              Analyze 12 years of Wega di Number Korsou data with advanced AI to discover patterns and generate intelligent predictions
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/predict">
                <Button size="lg" className="w-full sm:w-auto group">
                  <Sparkles className="w-5 h-5 mr-2 group-hover:animate-spin" />
                  Generate Predictions
                </Button>
              </Link>
              <Link href="/statistics">
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  <BarChart3 className="w-5 h-5 mr-2" />
                  View Statistics
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="w-full sm:w-auto"
                onClick={handleCheckUpdates}
                disabled={isUpdating}
              >
                <RefreshCw className={`w-5 h-5 mr-2 ${isUpdating ? 'animate-spin' : ''}`} />
                Check for Updates
              </Button>
            </div>
            
            {/* Last Update Info */}
            {lastUpdate && (
              <div className="mt-6 text-center text-sm text-muted-foreground">
                <p>Latest draw: <span className="font-semibold text-foreground">{lastUpdate.lastDrawDate}</span></p>
                {lastUpdate.lastScrapedAt && (() => {
                  try {
                    const date = new Date(lastUpdate.lastScrapedAt);
                    if (isNaN(date.getTime())) return null;
                    return <p className="mt-1">Last updated: {date.toLocaleString()}</p>;
                  } catch (e) {
                    return null;
                  }
                })()}
              </div>
            )}
            {lastUpdateError && (
              <div className="mt-6 text-center text-sm text-destructive">
                <p>Failed to load update info</p>
              </div>
            )}
          </div>

          {/* Next Draw Countdown */}
          <Card className="max-w-2xl mx-auto mb-12 bg-gradient-to-r from-card to-blue-950/30 border-primary/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Next Draw</p>
                    <p className="text-2xl font-bold text-primary">{getTimeUntilDraw()}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Daily at</p>
                  <p className="text-2xl font-bold">9:00 PM</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Grid */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <Card className="bg-gradient-to-br from-card to-blue-950/20 border-primary/10 hover:border-primary/30 transition-all">
                <CardHeader>
                  <CardTitle className="text-primary">Total Draws</CardTitle>
                  <CardDescription>Historical data analyzed</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold">{stats.totalDraws.toLocaleString()}</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-card to-blue-950/20 border-primary/10 hover:border-primary/30 transition-all">
                <CardHeader>
                  <CardTitle className="text-primary">AI Methods</CardTitle>
                  <CardDescription>Analysis techniques</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold">10+</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-card to-blue-950/20 border-primary/10 hover:border-primary/30 transition-all">
                <CardHeader>
                  <CardTitle className="text-primary">Years of Data</CardTitle>
                  <CardDescription>2014 - Present</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold">{new Date().getFullYear() - 2014 + 1}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Recent Draws */}
          <Card className="max-w-4xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Recent Draws
              </CardTitle>
              <CardDescription>Latest lottery results</CardDescription>
            </CardHeader>
            <CardContent>
              {recentDrawsError ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 text-destructive opacity-50" />
                  <p className="text-destructive mb-2">Failed to load recent draws</p>
                  <p className="text-sm text-muted-foreground">{recentDrawsError.message}</p>
                </div>
              ) : isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : recentDraws && recentDraws.length > 0 ? (
                <div className="space-y-4">
                  {recentDraws.map((draw) => (
                    <div
                      key={draw.id}
                      className="flex flex-col md:flex-row md:items-center md:justify-between p-4 rounded-lg bg-secondary/50 hover:bg-secondary transition-all gap-3"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                        <div className="text-sm text-muted-foreground font-mono">{draw.drawDate}</div>
                        <div className="flex gap-2 flex-wrap">
                          <div className="px-3 py-1 rounded-md bg-primary/20 text-primary font-bold border border-primary/30">
                            {draw.firstPrize}
                          </div>
                          <div className="px-3 py-1 rounded-md bg-blue-500/20 text-blue-400 font-bold border border-blue-500/30">
                            {draw.secondPrize}
                          </div>
                          <div className="px-3 py-1 rounded-md bg-purple-500/20 text-purple-400 font-bold border border-purple-500/30">
                            {draw.thirdPrize}
                          </div>
                        </div>
                      </div>
                      {draw.color && (
                        <div className="text-sm text-muted-foreground md:text-right">
                          {draw.color}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No data available. Admin needs to scrape historical data.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <Card className="max-w-4xl mx-auto mt-8 border-amber-500/30 bg-amber-500/5">
            <CardContent className="p-6">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-semibold text-foreground mb-1">Important Disclaimer</p>
                  <p>
                    Lottery numbers are fundamentally random. While our AI analyzes historical patterns using advanced techniques,
                    past performance does not guarantee future results. This application is for entertainment and educational purposes.
                    Please play responsibly and never bet more than you can afford to lose.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer with Version */}
      <footer className="py-6 mt-12 border-t border-border/40">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">
            Version {VERSION}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Fix #{FIX_NUMBER}
          </p>
        </div>
      </footer>
    </div>
  );
}
